import { useState, useEffect } from 'react';
import { resolveMediaUrl } from '@/utils/firebaseUtils';
import { InitialAssessment } from '@/services/noteGoApi/noteGoApiSchemas';
import { getInitialAssessmentFromContentAnalysis } from '@/utils/contentAnalysis';
import { logger } from '@/services/loggerService';

type UseContentAnalysisResult = {
  initialAssessment: InitialAssessment | undefined;
  isLoading: boolean;
};

/**
 * Derives the complete.json GCS path from a transcript URL.
 * e.g. "gs://bucket/userId/noteId/full_transcript.json" -> "gs://bucket/userId/noteId/complete.json"
 */
function deriveCompleteUrl(transcriptUrl: string): string {
  const lastSlash = transcriptUrl.lastIndexOf('/');
  return transcriptUrl.substring(0, lastSlash + 1) + 'complete.json';
}

/**
 * Fetches content analysis from the note data, falling back to complete.json
 * in GCS if the API returns null.
 */
const useFetchContentAnalysis = (
  contentAnalysis: Record<string, unknown> | null | undefined,
  transcriptUrl: string | null | undefined,
): UseContentAnalysisResult => {
  const [gcsAssessment, setGcsAssessment] = useState<InitialAssessment | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  // Try to extract from the API response first
  const apiAssessment = getInitialAssessmentFromContentAnalysis(
    contentAnalysis as Parameters<typeof getInitialAssessmentFromContentAnalysis>[0],
  );

  useEffect(() => {
    // Clear any stale fallback assessment from a previous note BEFORE any
    // early return — otherwise navigating from a note that set gcsAssessment
    // to one with content_analysis=null would leak the previous note's data.
    setGcsAssessment(undefined);

    // If the API already gave us the data, no need to fetch
    if (apiAssessment) return;
    // If no transcript URL to derive complete.json path from, nothing to fall back to
    if (!transcriptUrl) return;
    // Path derivation only works on gs:// URIs. Once the backend serves signed
    // CDN URLs, the string-surgery below would mangle the signature, so skip
    // the fallback entirely — the backend will return complete_url directly.
    if (!transcriptUrl.startsWith('gs://')) return;

    const completeUrl = deriveCompleteUrl(transcriptUrl);

    // Stale-response guard: an in-flight fetch from a previous note must not
    // overwrite the state that was just reset for the current note.
    let isActive = true;

    const fetchFromGCS = async () => {
      setIsLoading(true);
      try {
        const secureUrl = await resolveMediaUrl(completeUrl);
        if (!secureUrl) return;
        const response = await fetch(secureUrl);
        if (!response.ok) return;

        const data = await response.json();
        if (!isActive) return;
        if (!data || typeof data !== 'object') return;

        // Handle list-wrapped data
        const parsed = Array.isArray(data) && data.length > 0 ? data[0] : data;
        const ca = parsed?.content_analysis;
        if (!ca) return;

        const assessment = getInitialAssessmentFromContentAnalysis(ca);
        if (assessment) {
          logger.debug('notes', 'Content analysis loaded from GCS fallback', {
            keys: Object.keys(assessment),
          });
          setGcsAssessment(assessment);
        }
      } catch {
        // Silent failure — content analysis is non-critical
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    fetchFromGCS();

    return () => {
      isActive = false;
    };
  }, [apiAssessment, transcriptUrl]);

  return {
    initialAssessment: apiAssessment || gcsAssessment,
    isLoading: !apiAssessment && isLoading,
  };
};

export default useFetchContentAnalysis;
